import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { UserCircle, Shield, ShieldOff, ChevronDown, UserPlus, X, Mail, Trash2, Globe, Check } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const COUNTRY_OPTIONS = ['India', 'United States'];

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingCountryTags, setEditingCountryTags] = useState(null);
  const TIERS = ['free', 'trial_paid', 'soulai_paid', 'elite'];
  const toggleTier = async (u, tier) => {
    const current = u.lead_tier_eligibility || TIERS;
    const next = current.includes(tier) ? current.filter((t) => t !== tier) : [...current, tier];
    if (next.length === 0) { toast.error('An associate must accept at least one tier'); return; }
    try {
      await axios.put(`${API_URL}/api/users/${u.user_id}/tier-eligibility`, next, { withCredentials: true });
      toast.success('Lead tier eligibility updated');
      fetchUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  }; // user_id being edited
  const [selectedCountries, setSelectedCountries] = useState([]);

  const toggleAccepting = async (u) => {
    const next = !(u.accepting_leads !== false);
    try {
      await axios.put(`${API_URL}/api/users/${u.user_id}/accepting-leads`, { value: next }, { withCredentials: true });
      toast.success(next ? `${u.full_name} is back in the lead rotation` : `${u.full_name} will not receive new leads`);
      fetchUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const toggleTeamLead = async (u) => {
    const next = !u.is_team_lead;
    try {
      await axios.put(`${API_URL}/api/users/${u.user_id}/team-lead`, { value: next }, { withCredentials: true });
      toast.success(next ? `${u.full_name} is now a Team Leader` : `${u.full_name} is no longer a Team Leader`);
      fetchUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}/role?role=${newRole}`, {}, { withCredentials: true });
      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleSuspend = async (userId) => {
    const reason = window.prompt('Enter suspension reason (optional):');
    if (reason === null) return;
    
    try {
      await axios.put(`${API_URL}/api/users/${userId}/suspend?reason=${encodeURIComponent(reason)}`, {}, { withCredentials: true });
      toast.success('User suspended');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const handleActivate = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}/activate`, {}, { withCredentials: true });
      toast.success('User activated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate user');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${userName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`, { withCredentials: true });
      toast.success(`User ${userName} deleted successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleEditCountryTags = (user) => {
    setEditingCountryTags(user.user_id);
    setSelectedCountries(user.country_tags || []);
  };

  const handleSaveCountryTags = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}/country-tags`, selectedCountries, { withCredentials: true });
      toast.success('Country tags updated');
      setEditingCountryTags(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update country tags');
    }
  };

  const toggleCountry = (country) => {
    setSelectedCountries(prev => 
      prev.includes(country) 
        ? prev.filter(c => c !== country)
        : [...prev, country]
    );
  };

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: 'bg-purple-100 text-purple-700',
      finance_admin: 'bg-blue-100 text-blue-700',
      pre_sales: 'bg-green-100 text-green-700',
      sales: 'bg-amber-100 text-amber-700',
      operations: 'bg-slate-100 text-slate-700',
      pending: 'bg-red-100 text-red-700'
    };
    return colors[role] || 'bg-slate-100 text-slate-700';
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'badge-success',
      pending: 'badge-warning',
      suspended: 'badge-error'
    };
    return styles[status] || 'badge-pending';
  };

  const formatRole = (role) => {
    return role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const roles = ['finance_admin', 'pre_sales', 'sales', 'operations', 'marketing', 'doctor'];

  return (
    <div data-testid="users-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
          <p className="text-slate-500">Manage user roles and access</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="invite-user-btn"
        >
          <UserPlus className="w-4 h-4" />
          Invite User
        </button>
      </div>

      <div className="table-wrapper overflow-x-auto">
        <table className="w-full">
          <thead className="table-header">
            <tr>
              <th className="p-4 text-left">User</th>
              <th className="p-4 text-left">Role</th>
              <th className="p-4 text-left">Country Tags</th>
              <th className="p-4 text-left">Lead Tiers</th>
              <th className="p-4 text-left">Receiving Leads</th>
              <th className="p-4 text-left">Status</th>
              <th className="p-4 text-left">Last Login</th>
              <th className="p-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="p-8 text-center text-slate-500">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  </div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-8 text-center text-slate-500">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.user_id} className="table-row" data-testid={`user-row-${u.user_id}`}>
                  <td className="table-cell">
                    <div className="flex items-center gap-3">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt="" className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          <UserCircle className="w-6 h-6 text-indigo-600" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-slate-900">{u.full_name}</div>
                        <div className="text-sm text-slate-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="relative">
                      {u.role === 'super_admin' ? (
                        <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${getRoleBadge(u.role)}`}>
                          {formatRole(u.role)}
                        </span>
                      ) : (
                        <>
                          <select
                            value={u.role}
                            onChange={(e) => handleRoleChange(u.user_id, e.target.value)}
                            disabled={u.user_id === currentUser?.user_id}
                            className={`appearance-none px-3 py-1.5 pr-8 rounded-full text-xs font-medium cursor-pointer ${getRoleBadge(u.role)} border-0 focus:ring-2 focus:ring-indigo-500`}
                            data-testid={`role-select-${u.user_id}`}
                          >
                            {roles.map(role => (
                              <option key={role} value={role}>{formatRole(role)}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none" />
                        </>
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    {/* Country Tags - Only for pre_sales and sales */}
                    {['pre_sales', 'sales'].includes(u.role) ? (
                      editingCountryTags === u.user_id ? (
                        <div className="space-y-2">
                          <div className="flex flex-wrap gap-1">
                            {COUNTRY_OPTIONS.map(country => (
                              <button
                                key={country}
                                onClick={() => toggleCountry(country)}
                                className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                                  selectedCountries.includes(country)
                                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-indigo-300'
                                }`}
                              >
                                {selectedCountries.includes(country) && <Check className="w-3 h-3 inline mr-1" />}
                                {country === 'United States' ? 'US' : country === 'United Kingdom' ? 'UK' : country}
                              </button>
                            ))}
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleSaveCountryTags(u.user_id)}
                              className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingCountryTags(null)}
                              className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div 
                          onClick={() => handleEditCountryTags(u)}
                          className="cursor-pointer hover:bg-slate-50 rounded p-1 -m-1"
                        >
                          {u.country_tags && u.country_tags.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {u.country_tags.map(tag => (
                                <span key={tag} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                                  {tag === 'United States' ? 'US' : tag === 'United Kingdom' ? 'UK' : tag}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              All countries (click to set)
                            </span>
                          )}
                        </div>
                      )
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="table-cell" data-testid={`tier-eligibility-${u.user_id}`}>
                    {['pre_sales', 'sales'].includes(u.role) ? (
                      <div className="flex flex-wrap gap-1">
                        {TIERS.map((t) => {
                          const on = !u.lead_tier_eligibility || u.lead_tier_eligibility.includes(t);
                          return (
                            <button key={t} type="button" title={`${on ? 'Accepts' : 'Skips'} ${t} leads in round robin`}
                              onClick={() => toggleTier(u, t)}
                              className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border transition-colors ${on ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-400 line-through'}`}
                              data-testid={`tier-toggle-${u.user_id}-${t}`}>
                              {t.replace('_', ' ')}
                            </button>
                          );
                        })}
                      </div>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="table-cell" data-testid={`accepting-cell-${u.user_id}`}>
                    {['pre_sales', 'sales'].includes(u.role) ? (
                      <div className="flex flex-col gap-1.5">
                        <button type="button" role="switch" aria-checked={u.accepting_leads !== false}
                          onClick={() => toggleAccepting(u)}
                          title={u.accepting_leads !== false ? 'Receiving new leads — click to pause (leave / capacity)' : 'Paused — click to resume'}
                          className="inline-flex items-center gap-2 text-xs font-medium"
                          data-testid={`accepting-toggle-${u.user_id}`}>
                          <span className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${u.accepting_leads !== false ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${u.accepting_leads !== false ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </span>
                          <span className={u.accepting_leads !== false ? 'text-emerald-700' : 'text-slate-500'}>{u.accepting_leads !== false ? 'On' : 'Off'}</span>
                        </button>
                        {u.role === 'sales' && (
                          <button type="button" onClick={() => toggleTeamLead(u)}
                            title={u.is_team_lead ? 'Team Leader — can transfer any sales lead. Click to remove.' : 'Mark as Team Leader'}
                            className={`self-start px-2 py-0.5 text-[10px] font-semibold rounded-full border transition-colors ${u.is_team_lead ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-slate-50 border-slate-200 text-slate-400 hover:text-slate-600'}`}
                            data-testid={`team-lead-toggle-${u.user_id}`}>
                            {u.is_team_lead ? 'Team Leader' : 'Make TL'}
                          </button>
                        )}
                      </div>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="table-cell">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(u.status)}`}>
                      {u.status.toUpperCase()}
                    </span>
                    {u.suspension_reason && (
                      <div className="text-xs text-slate-500 mt-1">
                        Reason: {u.suspension_reason}
                      </div>
                    )}
                  </td>
                  <td className="table-cell">
                    <span className="text-sm text-slate-500">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'}
                    </span>
                  </td>
                  <td className="table-cell">
                    {u.user_id !== currentUser?.user_id && (
                      <div className="flex items-center gap-1">
                        {u.status === 'suspended' ? (
                          <>
                            <button
                              onClick={() => handleActivate(u.user_id)}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-md"
                              data-testid={`activate-user-${u.user_id}`}
                            >
                              <Shield className="w-4 h-4" />
                              Activate
                            </button>
                            <button
                              onClick={() => handleDelete(u.user_id, u.full_name)}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md"
                              title="Delete user permanently"
                              data-testid={`delete-user-${u.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleSuspend(u.user_id)}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md"
                            data-testid={`suspend-user-${u.user_id}`}
                          >
                            <ShieldOff className="w-4 h-4" />
                            Suspend
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Invite User Modal */}
      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            fetchUsers();
          }}
          roles={roles}
        />
      )}
    </div>
  );
};

// Invite User Modal Component
const InviteUserModal = ({ onClose, onSuccess, roles }) => {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'sales'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.role) {
      toast.error('Email and role are required');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/users/invite`, formData, { withCredentials: true });
      toast.success(`Invitation sent to ${formData.email}`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    } finally {
      setSaving(false);
    }
  };

  const formatRole = (role) => {
    return role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Invite New User</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email Address *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-base pl-10"
                placeholder="user@example.com"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              The user will be able to login with this Google account email
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Full Name (Optional)</label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="input-base"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Pre-assign Role *</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="input-base"
            >
              {roles.map(role => (
                <option key={role} value={role}>{formatRole(role)}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              The user will automatically get this role when they first login
            </p>
          </div>

          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              <strong>How it works:</strong> The user will be pre-approved with the selected role. 
              When they login for the first time using Google OAuth with this email, 
              they'll have immediate access.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
              {saving ? 'Inviting...' : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Send Invite
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsersPage;
